import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Users, Building2, CheckCircle, XCircle, UserPlus, Mail, KeyRound } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';

interface TeamInvitationData {
  id: string;
  team_id: string;
  invited_email: string;
  invited_by: string;
  status: string;
  expires_at: string;
  team: {
    name: string;
    team_code: string;
    organization_id: string;
  };
  inviter_profile: {
    first_name: string | null;
    last_name: string | null;
  } | null;
  organization: {
    name: string;
    color: string | null;
    logo_url: string | null;
  } | null;
}

interface CustomField {
  id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
  placeholder: string | null;
}

const TeamInvitation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const invitationIdFromUrl = searchParams.get('id');
  const invitationId = invitationIdFromUrl || (() => {
    try {
      return localStorage.getItem('last_team_invitation_id');
    } catch {
      return null;
    }
  })();

  // Persist the invitation id so the flow can resume even if the redirect loses query params
  useEffect(() => {
    if (!invitationIdFromUrl) return;
    try {
      localStorage.setItem('last_team_invitation_id', invitationIdFromUrl);
    } catch {
      // ignore
    }
  }, [invitationIdFromUrl]);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState<TeamInvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'loading' | 'signup' | 'verify' | 'profile' | 'success' | 'error'>('loading');
  const [otpCode, setOtpCode] = useState('');
  const [isProcessingMagicLink, setIsProcessingMagicLink] = useState(false);
  const [verificationType, setVerificationType] = useState<'signup' | 'email'>('signup');
  
  // Profile form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: ''
  });
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  // Handle magic link callback (hash access_token OR PKCE ?code=)
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    const handleMagicLinkCallback = async () => {
      const url = new URL(window.location.href);
      const hash = url.hash || '';
      const code = url.searchParams.get('code');
      const hasAccessToken = hash.includes('access_token=');

      if (!code && !hasAccessToken) return;

      console.log('Magic link callback detected (code/hash)', { code: !!code, hasAccessToken });
      setIsProcessingMagicLink(true);

      try {
        // If PKCE code is present, explicitly exchange it for a session.
        if (code) {
          console.log('Exchanging PKCE code for session...');
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (cancelled) return;

          if (error) {
            console.error('exchangeCodeForSession error:', error);
            toast.error('Login-Link ist ungültig oder abgelaufen. Bitte Code eingeben oder erneut senden.');
            setIsProcessingMagicLink(false);
            return;
          }

          console.log('Session exchange successful:', !!data.session);
          
          // Remove one-time params to prevent re-processing on refresh
          url.searchParams.delete('code');
          url.searchParams.delete('type');
          url.searchParams.delete('redirect_to');
          const next = `${url.pathname}?${url.searchParams.toString()}${url.hash}`.replace('?&', '?').replace(/\?$/, '');
          window.history.replaceState({}, document.title, next);
          
          // Session was set, the auth listener should pick it up
          // Give it a moment then check
          await new Promise(resolve => setTimeout(resolve, 500));
          const { data: sessionCheck } = await supabase.auth.getSession();
          if (sessionCheck.session?.user) {
            console.log('Session confirmed after code exchange');
            // Don't set isProcessingMagicLink to false - let the pending invitation handler do it
            return;
          }
        }

        // Handle hash-based tokens (older Supabase flow)
        if (hasAccessToken && !code) {
          console.log('Processing hash-based access token...');
          // The Supabase client should auto-process this via onAuthStateChange
          // Wait a bit for it to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          const { data: sessionCheck } = await supabase.auth.getSession();
          if (sessionCheck.session?.user) {
            console.log('Session confirmed from hash token');
            return;
          }
        }

        // Safety net: if we still don't have a session after waiting, stop the spinner
        timeoutId = window.setTimeout(async () => {
          if (cancelled) return;
          const { data } = await supabase.auth.getSession();
          if (data.session?.user) {
            console.log('Session confirmed in timeout check');
            return;
          }

          console.warn('Magic link processing timeout - no session created');
          toast.error('Anmeldung konnte nicht abgeschlossen werden. Bitte Code verwenden oder Link erneut senden.');
          setIsProcessingMagicLink(false);
        }, 8000);
      } catch (err) {
        console.error('Magic link callback error:', err);
        toast.error('Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.');
        setIsProcessingMagicLink(false);
      }
    };

    void handleMagicLinkCallback();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  // Process pending invitation data after magic link auth completes
  useEffect(() => {
    const processPendingInvitation = async () => {
      if (!user || !invitationId) return;
      
      // Check if we have pending invitation data from before the magic link redirect
      const pendingDataStr = localStorage.getItem('pending_team_invitation_data');
      if (!pendingDataStr) {
        // If localStorage isn't available (different device/browser context), try to resume from
        // auth metadata so the user does NOT have to re-enter everything.
        setIsProcessingMagicLink(true);
        try {
          const { data: invData, error: invError } = await supabase.functions.invoke('get-invitation-details', {
            body: { invitationId }
          });

          if (invError || invData?.error) {
            console.error('Error fetching invitation while resuming:', invError || invData?.error);
            setStep('profile');
            return;
          }

          const meta: any = (user.user_metadata ?? {});
          const derived = {
            first_name: String(meta.first_name ?? ''),
            last_name: String(meta.last_name ?? ''),
            phone: String(meta.phone ?? ''),
            address: String(meta.address ?? ''),
          };

          // Prefill whatever we can for a smoother fallback
          setFormData((prev) => ({
            ...prev,
            first_name: derived.first_name || prev.first_name,
            last_name: derived.last_name || prev.last_name,
            phone: derived.phone || prev.phone,
            address: derived.address || prev.address,
          }));

          const requiredCustomFields: any[] = (invData.customFields || []).filter((f: any) => f.is_required);
          const missingCore = !derived.first_name.trim() || !derived.last_name.trim() || !derived.phone.trim();
          const canAutoAccept = !missingCore && requiredCustomFields.length === 0;

          if (!canAutoAccept) {
            // We can't safely auto-accept (missing required fields). Continue in profile step,
            // but at least it's prefilled.
            setStep('profile');
            return;
          }

          // Auto-complete the invitation using metadata
          const email = (user.email || invData.invitation?.invited_email || '').toLowerCase();
          const { error: profileError } = await supabase
            .from('profiles')
            .upsert({
              user_id: user.id,
              email,
              first_name: derived.first_name.trim(),
              last_name: derived.last_name.trim(),
              phone: derived.phone.trim(),
              address: derived.address.trim() || null,
              team_id: invData.invitation.team_id,
              organization_id: invData.team.organization_id,
              onboarding_completed: true,
            }, { onConflict: 'user_id' });

          if (profileError) {
            console.error('Error updating profile during resume:', profileError);
            setStep('profile');
            return;
          }

          await supabase
            .from('team_member_roles')
            .upsert({
              team_id: invData.invitation.team_id,
              user_id: user.id,
              role: 'member',
            }, { onConflict: 'user_id,team_id' });

          await supabase
            .from('team_invitations')
            .update({
              status: 'accepted',
              updated_at: new Date().toISOString(),
            })
            .eq('id', invitationId);

          toast.success('Willkommen im Team!');
          setTimeout(() => {
            window.location.href = '/';
          }, 300);
        } catch (err) {
          console.error('Error resuming invitation without pending data:', err);
          setStep('profile');
        } finally {
          setIsProcessingMagicLink(false);
        }
        return;
      }
      
      try {
        const pendingData = JSON.parse(pendingDataStr);
        
        // Make sure this is for the same invitation
        if (pendingData.invitationId !== invitationId) return;
        
        console.log('Processing pending invitation data after magic link auth');
        setIsProcessingMagicLink(true);
        
        // Fetch fresh invitation data
        const { data: invData, error: invError } = await supabase.functions.invoke('get-invitation-details', {
          body: { invitationId }
        });
        
        if (invError || invData?.error) {
          console.error('Error fetching invitation for pending data:', invError || invData?.error);
          localStorage.removeItem('pending_team_invitation_data');
          setIsProcessingMagicLink(false);
          return;
        }
        
        // Update profile with stored form data
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            user_id: user.id,
            email: pendingData.email,
            first_name: pendingData.formData.first_name,
            last_name: pendingData.formData.last_name,
            phone: pendingData.formData.phone,
            address: pendingData.formData.address,
            team_id: invData.invitation.team_id,
            organization_id: invData.team.organization_id,
            onboarding_completed: true
          }, { onConflict: 'user_id' });

        if (profileError) {
          console.error('Error updating profile:', profileError);
        }
        
        // Save custom field values
        if (pendingData.customFieldValues) {
          for (const [fieldId, value] of Object.entries(pendingData.customFieldValues)) {
            if (value && String(value).trim()) {
              await supabase
                .from('team_member_custom_field_values')
                .upsert({
                  field_id: fieldId,
                  user_id: user.id,
                  value: String(value).trim()
                }, { onConflict: 'field_id,user_id' });
            }
          }
        }
        
        // Assign team member role
        await supabase
          .from('team_member_roles')
          .upsert({
            team_id: invData.invitation.team_id,
            user_id: user.id,
            role: 'member'
          }, { onConflict: 'user_id,team_id' });
        
        // Mark invitation as accepted
        await supabase
          .from('team_invitations')
          .update({ 
            status: 'accepted',
            updated_at: new Date().toISOString()
          })
          .eq('id', invitationId);
        
        // Clean up
        localStorage.removeItem('pending_team_invitation_data');
        
        toast.success('Willkommen im Team!');
        
        // Redirect to home
        setTimeout(() => {
          window.location.href = '/';
        }, 500);
        
      } catch (err) {
        console.error('Error processing pending invitation:', err);
        localStorage.removeItem('pending_team_invitation_data');
      } finally {
        setIsProcessingMagicLink(false);
      }
    };

    if (!authLoading && user) {
      processPendingInvitation();
    }
  }, [user, authLoading, invitationId]);

  // Fetch invitation data via edge function (bypasses RLS for public access)
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!invitationId) {
        setError('Keine Einladungs-ID gefunden');
        setStep('error');
        setLoading(false);
        return;
      }

      try {
        // Use edge function to fetch all invitation data (bypasses RLS)
        const { data, error: fetchError } = await supabase.functions.invoke('get-invitation-details', {
          body: { invitationId }
        });

        if (fetchError) {
          console.error('Error fetching invitation:', fetchError);
          setError('Fehler beim Laden der Einladung');
          setStep('error');
          setLoading(false);
          return;
        }

        if (data?.error) {
          // Handle specific error codes
          if (data.code === 'NOT_FOUND') {
            setError('Einladung nicht gefunden');
          } else if (data.code === 'EXPIRED') {
            setError('Diese Einladung ist abgelaufen');
          } else if (data.code === 'ACCEPTED') {
            setError('Diese Einladung wurde bereits angenommen');
          } else if (data.code === 'DECLINED') {
            setError('Diese Einladung wurde bereits abgelehnt');
          } else {
            setError(data.error);
          }
          setStep('error');
          setLoading(false);
          return;
        }

        setCustomFields(data.customFields || []);
        
        setInvitation({
          id: data.invitation.id,
          team_id: data.invitation.team_id,
          invited_email: data.invitation.invited_email,
          invited_by: '',
          status: data.invitation.status,
          expires_at: data.invitation.expires_at,
          team: data.team,
          inviter_profile: data.inviterProfile,
          organization: data.organization
        });

        // Determine next step based on auth state
        if (authLoading) {
          setStep('loading');
        } else if (!user) {
          setStep('signup');
        } else {
          // Pre-fill form with existing profile data
          const { data: profileData } = await supabase
            .from('profiles')
            .select('first_name, last_name, phone, address')
            .eq('user_id', user.id)
            .single();

          if (profileData) {
            setFormData(prev => ({
              ...prev,
              first_name: profileData.first_name || '',
              last_name: profileData.last_name || '',
              phone: profileData.phone || '',
              address: profileData.address || ''
            }));
          }
          setStep('profile');
        }
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError('Fehler beim Laden der Einladung');
        setStep('error');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [invitationId, user, authLoading]);

  // Handle auth state changes
  useEffect(() => {
    if (!authLoading && invitation) {
      // Don't override verify step - user is in verification flow
      if (step === 'verify') return;
      
      if (!user) {
        setStep('signup');
      } else if (step === 'signup') {
        setStep('profile');
      }
    }
  }, [user, authLoading, invitation, step]);

  const handleSignup = async () => {
    if (!invitation) return;

    // Validate required fields
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('Bitte geben Sie Vor- und Nachnamen ein');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Bitte geben Sie eine Telefonnummer ein');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Bitte geben Sie Ihre Adresse ein');
      return;
    }

    // Validate custom required fields
    for (const field of customFields) {
      if (field.is_required && !customFieldValues[field.id]?.trim()) {
        toast.error(`Bitte füllen Sie das Feld "${field.field_name}" aus`);
        return;
      }
    }

    setProcessing(true);
    try {
      // Persist invitation id for resilience across redirect/browser contexts
      try {
        localStorage.setItem('last_team_invitation_id', invitation.id);
      } catch {
        // ignore
      }

      // Store form data in localStorage BEFORE signup so it persists across the magic link redirect
      localStorage.setItem('pending_team_invitation_data', JSON.stringify({
        invitationId: invitation.id,
        email: invitation.invited_email,
        formData: {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
          address: formData.address.trim()
        },
        customFieldValues
      }));

      // Generate a random password (user will use magic link to login)
      const randomPassword = crypto.randomUUID() + crypto.randomUUID();
      const redirectUrl = `${window.location.origin}/team-invitation?id=${invitation.id}`;

      // Create user account with random password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.invited_email,
        password: randomPassword,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone: formData.phone.trim(),
            address: formData.address.trim()
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          // User exists - send them a magic link instead
          const { error: magicLinkError } = await supabase.auth.signInWithOtp({
            email: invitation.invited_email,
            options: {
              emailRedirectTo: redirectUrl,
              shouldCreateUser: false
            }
          });
          
          if (magicLinkError) {
            toast.error('Fehler beim Senden des Login-Links: ' + magicLinkError.message);
            setProcessing(false);
            return;
          }
          
          toast.success('Login-Link gesendet! Bitte prüfen Sie Ihr Postfach.');
          setVerificationType('email');
          setStep('verify');
          return;
        } else {
          // Real error: remove pending data to avoid stale state
          localStorage.removeItem('pending_team_invitation_data');
          toast.error('Fehler bei der Registrierung: ' + authError.message);
        }
        setProcessing(false);
        return;
      }

      // Handle "user_repeated_signup" case: user exists but unverified
      // Supabase returns success but with empty identities array
      if (authData.user && authData.user.identities?.length === 0) {
        console.log('User exists but unverified - resending confirmation email');
        // IMPORTANT: Do NOT rely on Supabase Auth email delivery here.
        // Send a fresh magic link via our own SMTP (edge function).
        const { data: resendData, error: resendInvokeError } = await supabase.functions.invoke(
          'team-invitation-resend-login-link',
          {
            body: {
              invitationId: invitation.id,
              redirectTo: redirectUrl,
            },
          }
        );

        if (resendInvokeError || resendData?.error) {
          console.error('Resend login link error:', resendInvokeError || resendData?.error);
          toast.error('Fehler beim erneuten Senden. Bitte später erneut versuchen.');
          setProcessing(false);
          return;
        }

        toast.success('Neuer Code/Link wurde gesendet!');
        // We now send a magic-link/OTP email.
        setVerificationType('email');
        setStep('verify');
        return;
      }

      if (!authData.user) {
        localStorage.removeItem('pending_team_invitation_data');
        toast.error('Fehler bei der Registrierung');
        setProcessing(false);
        return;
      }
      setVerificationType('signup');
      
      // Move to verification step
      toast.success('Bestätigungs-E-Mail gesendet! Bitte prüfen Sie Ihr Postfach.');
      setStep('verify');

    } catch (err) {
      console.error('Signup error:', err);
      localStorage.removeItem('pending_team_invitation_data');
      toast.error('Fehler bei der Registrierung');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!invitation || otpCode.length !== 6) {
      toast.error('Bitte geben Sie den 6-stelligen Code ein');
      return;
    }

    setProcessing(true);
    try {
      // Verify the OTP code
      const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
        email: invitation.invited_email,
        token: otpCode,
        type: verificationType
      });

      if (verifyError) {
        toast.error('Ungültiger Code. Bitte versuchen Sie es erneut.');
        setProcessing(false);
        return;
      }

      if (!verifyData.user) {
        toast.error('Verifizierung fehlgeschlagen');
        setProcessing(false);
        return;
      }

      // Wait a moment for the profile trigger to create the profile
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Upsert profile - create if not exists, update if exists
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          user_id: verifyData.user.id,
          email: invitation.invited_email,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
          address: formData.address.trim(),
          team_id: invitation.team_id,
          organization_id: invitation.team.organization_id,
          onboarding_completed: true
        }, { onConflict: 'user_id' });

      if (profileError) {
        console.error('Profile upsert error:', profileError);
        toast.error('Profil konnte nicht erstellt werden');
        setProcessing(false);
        return;
      }

      // Save custom field values
      for (const field of customFields) {
        const value = customFieldValues[field.id] || '';
        if (value.trim()) {
          await supabase
            .from('team_member_custom_field_values')
            .upsert({
              field_id: field.id,
              user_id: verifyData.user.id,
              value: value.trim()
            }, { onConflict: 'field_id,user_id' });
        }
      }

      // Assign team member role
      await supabase
        .from('team_member_roles')
        .insert({
          team_id: invitation.team_id,
          user_id: verifyData.user.id,
          role: 'member'
        });

      // Mark invitation as accepted
      await supabase
        .from('team_invitations')
        .update({ 
          status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      toast.success('Willkommen im Team!');
      
      // Wait a moment for profile update to propagate, then redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 500);

    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Fehler bei der Verifizierung');
    } finally {
      setProcessing(false);
    }
  };

  const handleResendCode = async () => {
    if (!invitation) return;
    
    setProcessing(true);
    try {
      // Use the invitation object id (URL param can get lost on some mobile email flows)
      const redirectTo = `${window.location.origin}/team-invitation?id=${invitation.id}`;
      
      console.log('Resending code/link, verificationType:', verificationType);

      // Always send via our own SMTP (edge function), because Supabase Auth resend can return 200
      // while the email never arrives (provider suppression/rate-limits).
      const { data, error } = await supabase.functions.invoke('team-invitation-resend-login-link', {
        body: {
          invitationId: invitation.id,
          redirectTo,
        },
      });

      if (error || data?.error) {
        console.error('Resend invoke failed:', error || data?.error);
        toast.error('Fehler beim erneuten Senden. Bitte später erneut versuchen.');
        return;
      }

      // This email is a magic-link/OTP email.
      setVerificationType('email');
      toast.success('Neuer Code/Link wurde gesendet!');
    } catch (err) {
      console.error('Resend error:', err);
      toast.error('Fehler beim erneuten Senden');
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!user || !invitation) return;

    // Validate required fields
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.phone.trim()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate custom required fields
    for (const field of customFields) {
      if (field.is_required && !customFieldValues[field.id]?.trim()) {
        toast.error(`Bitte füllen Sie das Feld "${field.field_name}" aus`);
        return;
      }
    }

    setProcessing(true);
    try {
      // Update user profile with team AND organization
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
          address: formData.address.trim() || null,
          team_id: invitation.team_id,
          organization_id: invitation.team.organization_id,
          onboarding_completed: true
        })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // Save custom field values
      for (const field of customFields) {
        const value = customFieldValues[field.id] || '';
        if (value.trim()) {
          await supabase
            .from('team_member_custom_field_values')
            .upsert({
              field_id: field.id,
              user_id: user.id,
              value: value.trim()
            }, { onConflict: 'field_id,user_id' });
        }
      }

      // Add default member role
      await supabase
        .from('team_member_roles')
        .insert({
          user_id: user.id,
          team_id: invitation.team_id,
          role: 'member'
        });

      // Update invitation status
      const { error: inviteError } = await supabase
        .from('team_invitations')
        .update({ 
          status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      if (inviteError) throw inviteError;

      setStep('success');
      toast.success('Willkommen im Team!');

      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error('Fehler beim Annehmen der Einladung');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;
    
    setProcessing(true);
    try {
      await supabase
        .from('team_invitations')
        .update({ 
          status: 'declined',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      toast.success('Einladung abgelehnt');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error declining invitation:', err);
      toast.error('Fehler beim Ablehnen der Einladung');
    } finally {
      setProcessing(false);
    }
  };

  const orgColor = invitation?.organization?.color || '#dc2626';

  // Show loading while processing magic link callback
  if (isProcessingMagicLink) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-muted-foreground">Anmeldung wird abgeschlossen...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'loading' || loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="mx-auto h-16 w-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Einladung ungültig</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Zur Startseite</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="mx-auto h-16 w-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">E-Mail gesendet!</h2>
            <p className="text-muted-foreground mb-6">
              Wir haben einen Login-Link an <strong>{invitation?.invited_email}</strong> gesendet. 
              Klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren und dem Team beizutreten.
            </p>
            <p className="text-sm text-muted-foreground">
              Bitte prüfen Sie auch Ihren Spam-Ordner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div 
              className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${orgColor}20` }}
            >
              <KeyRound className="h-8 w-8" style={{ color: orgColor }} />
            </div>
            <CardTitle>E-Mail bestätigen</CardTitle>
            <CardDescription>
              Wir haben eine E-Mail an <strong>{invitation?.invited_email}</strong> gesendet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Option 1: Click the link */}
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm font-medium mb-2">Option 1: Link in E-Mail klicken</p>
              <p className="text-xs text-muted-foreground">
                Klicken Sie einfach auf den Link in der E-Mail - Sie werden automatisch angemeldet.
              </p>
            </div>
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">oder</span>
              </div>
            </div>
            
            {/* Option 2: Enter code */}
            <div>
              <p className="text-sm font-medium text-center mb-3">Option 2: Code eingeben</p>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => setOtpCode(value)}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              Bitte prüfen Sie auch Ihren Spam-Ordner.
            </p>

            <div className="space-y-3">
              <Button 
                onClick={handleVerifyOtp}
                disabled={processing || otpCode.length !== 6}
                className="w-full"
                style={{ backgroundColor: orgColor }}
              >
                {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <CheckCircle className="h-4 w-4 mr-2" />
                Bestätigen
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleResendCode}
                disabled={processing}
                className="w-full"
              >
                Code erneut senden
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'signup') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div 
              className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${orgColor}20` }}
            >
              {invitation?.organization?.logo_url ? (
                <img 
                  src={invitation.organization.logo_url} 
                  alt={invitation.organization.name}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Building2 className="h-8 w-8" style={{ color: orgColor }} />
              )}
            </div>
            <CardTitle>Konto erstellen</CardTitle>
            <CardDescription>
              {invitation?.inviter_profile?.first_name} {invitation?.inviter_profile?.last_name} lädt Sie ein, dem Team "{invitation?.team?.name}" beizutreten
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Team info */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{invitation?.team?.name}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Organisation: {invitation?.organization?.name}
              </p>
            </div>

            {/* Email (read-only, from invitation) */}
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={invitation?.invited_email || ''}
                  disabled
                  className="pl-10 bg-muted"
                />
              </div>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Vorname *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Max"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nachname *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Mustermann"
                  required
                />
              </div>
            </div>
            
            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 123 456789"
                required
              />
            </div>
            
            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Musterstraße 1, 12345 Stadt"
                required
              />
            </div>


            {/* Custom team fields */}
            {customFields.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <Label className="text-xs text-muted-foreground">Team-spezifische Felder</Label>
                {customFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={`custom-${field.id}`}>
                      {field.field_name} {field.is_required && '*'}
                    </Label>
                    <Input
                      id={`custom-${field.id}`}
                      type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : 'text'}
                      value={customFieldValues[field.id] || ''}
                      onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                      placeholder={field.placeholder || `${field.field_name} eingeben...`}
                      required={field.is_required}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={handleDecline}
                disabled={processing}
                className="flex-1"
              >
                Ablehnen
              </Button>
              <Button 
                onClick={handleSignup}
                disabled={processing}
                className="flex-1"
                style={{ backgroundColor: orgColor }}
              >
                {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <UserPlus className="h-4 w-4 mr-2" />
                Beitreten
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Profile step (for already logged in users)
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div 
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: `${orgColor}20` }}
          >
            {invitation?.organization?.logo_url ? (
              <img 
                src={invitation.organization.logo_url} 
                alt={invitation.organization.name}
                className="h-8 w-8 object-contain"
              />
            ) : (
              <Building2 className="h-8 w-8" style={{ color: orgColor }} />
            )}
          </div>
          <CardTitle>Willkommen bei {invitation?.team?.name}!</CardTitle>
          <CardDescription>
            {invitation?.inviter_profile?.first_name} {invitation?.inviter_profile?.last_name} hat Sie eingeladen.
            Bitte vervollständigen Sie Ihr Profil.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Standard fields */}
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Vorname *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Max"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nachname *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Mustermann"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 123 456789"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Musterstraße 1, 12345 Stadt"
                required
              />
            </div>
          </div>

          {/* Custom team fields */}
          {customFields.length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-xs text-muted-foreground">Team-spezifische Felder</Label>
              {customFields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={`custom-${field.id}`}>
                    {field.field_name} {field.is_required && '*'}
                  </Label>
                  <Input
                    id={`custom-${field.id}`}
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : 'text'}
                    value={customFieldValues[field.id] || ''}
                    onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                    placeholder={field.placeholder || `${field.field_name} eingeben...`}
                    required={field.is_required}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={handleDecline}
              disabled={processing}
              className="flex-1"
            >
              Ablehnen
            </Button>
            <Button 
              onClick={handleAcceptInvitation}
              disabled={processing}
              className="flex-1"
              style={{ backgroundColor: orgColor }}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Beitreten
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamInvitation;
